import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Avatar } from '@/design-system'
import { isValidAvatarUrl } from '@/utils/helper'
import { avatarGradient } from '../../utils'

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '13px 18px',
  transition: `background var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': { background: 'var(--ds-hover)' },
})

const Info = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  minWidth: 0,
  flex: 1,
  cursor: 'pointer',
  textAlign: 'left',
})

const NameRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' })
const Name = styled(Typography)({ fontSize: 14, fontWeight: 660, letterSpacing: '-0.012em', color: 'var(--ds-ink)' })
const CircleBadge = styled('span')({
  fontSize: 9.5,
  fontWeight: 650,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--ds-accent-deep-b, #8b5cf6)',
  background: 'color-mix(in srgb, var(--ds-accent-dot-3) 12%, transparent)',
  padding: '1.5px 7px',
  borderRadius: 'var(--ds-radius-pill)',
})
const Meta = styled(Typography)({
  fontSize: 12,
  color: 'var(--ds-ink-muted)',
  marginTop: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  '& .dot': { width: 3, height: 3, borderRadius: '50%', background: 'var(--ds-ink-subtle)', flexShrink: 0 },
})
const SubLine = styled(Typography)({ fontSize: 11.5, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const Actions = styled(Box)({ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 })

export interface PersonRowProps {
  avatarUrl?: string
  name: string
  profession?: string
  location?: string
  /** Circle name badge shown next to the name, e.g. "Family" */
  circleLabel?: string
  /** e.g. "Wants to connect · 2 days ago", shown under profession/location */
  subLine?: React.ReactNode
  actions?: React.ReactNode
  onClick?: () => void
}

export function PersonRow({ avatarUrl, name, profession, location, circleLabel, subLine, actions, onClick }: PersonRowProps) {
  const initial = name[0]?.toUpperCase() ?? '?'
  const metaParts = [profession, location].filter(Boolean)
  const resolvedSrc = avatarUrl && isValidAvatarUrl(avatarUrl) ? avatarUrl : undefined

  return (
    <Row>
      <Info onClick={onClick} role={onClick ? 'button' : undefined}>
        <Avatar
          src={resolvedSrc}
          sx={{
            width: 46,
            height: 46,
            fontSize: 16,
            fontWeight: 700,
            background: avatarGradient(name),
            color: 'var(--ds-accent-ink)',
            flexShrink: 0,
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <NameRow>
            <Name noWrap>{name}</Name>
            {circleLabel && <CircleBadge>{circleLabel}</CircleBadge>}
          </NameRow>
          {metaParts.length > 0 && (
            <Meta noWrap>
              {metaParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className='dot' />}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </Meta>
          )}
          {subLine && <SubLine noWrap>{subLine}</SubLine>}
        </Box>
      </Info>
      {actions && <Actions>{actions}</Actions>}
    </Row>
  )
}
